<script>
	// imports
	import model from "../../stores/modelStore";
	import lookups from "../../stores/lookupStore";
	import { projectSettings, formTypes, genderTypes } from "../../config";
	import { isValid } from "../../validation";
	import { Tooltip } from "@svelte-plugins/tooltips";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import TextInput from "../../components/TextInput.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import SaveAndClose from "../../components/SaveAndClose.svelte";
	import ClaimDetails from "../../models/ClaimDetails";
	import Position from "../../models/Position";
	import Employment from "../../models/Employment";
	import EconomicLoss from "../../models/EconomicLoss";
	import ExperiencedAfter from "../../models/ExperiencedAfter";
	import DatePicker from "../../components/DatePicker.svelte";
	import Select from "../../components/Select.svelte";
	import AddButton from "../../components/buttons/AddButton.svelte";
	import RemoveButton from "../../components/buttons/RemoveButton.svelte";
	import PrimaryButton from "../../components/buttons/PrimaryButton.svelte";
	import Checkbox from "../../components/Checkbox.svelte";
	import registrationTypes from "../../config/registrationTypes";
	import RadioGroup from "../../components/RadioGroup.svelte";

	// props
	export let persistUrl = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;
	export let title = null;

	let buttons = null;

	let selectedEmploymentIndex = 0;
	let selectedEmployment = null;
	let showError = false;
	let modelErrors = [];
	let noneOfAboveId = null;
	let dummyValue = 0;

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	const validateEmploymentsAndPositions = () => {
		const errors = [];
		$model.claimDetails.employments.forEach((employment, employmentIndex) => {
			employmentIndex = employmentIndex + 1;

			// Validate Employment fields
			if (!employment.stateId) {
				errors.push({
					employmentIndex: employmentIndex,
					error: `Employment ${employmentIndex} requires a state selection.`
				});
			}
			if (!employment.employerName) {
				errors.push({
					employmentIndex: employmentIndex,
					error: `Employment ${employmentIndex} requires name.`
				});
			}
			if (!employment.harrassmentDetails) {
				errors.push({
					employmentIndex: employmentIndex,
					error: `Employment ${employmentIndex} requires harassment details.`
				});
			}
			if (employment.harassmentReported === "Yes" && !employment.harrassmentReportedTo) {
				errors.push({
					employmentIndex: employmentIndex,
					error: `Employment ${employmentIndex} requires information on who harassment was reported to.`
				});
			}
			if (employment.sufferEconomicLoss === "Yes" && !employment.sufferEconomicLoss) {
				errors.push({
					employmentIndex: employmentIndex,
					error: `Employment ${employmentIndex} requires details on economic loss.`
				});
			}
			console.log("Experienced after verify", employment.experiencedAfter);
			if (!employment.experiencedAfter || employment.experiencedAfter.length < 1) {
				errors.push({
					employmentIndex: employmentIndex,
					error: `Employment ${employmentIndex} requires details on experienced after.`
				});
			}

			employment.positions.forEach((position, positionIndex) => {
				positionIndex = positionIndex + 1;

				// Validate Position fields
				if (!position.positionName) {
					errors.push({
						employmentIndex: employmentIndex,
						error: `Position ${positionIndex} in Employment ${employmentIndex} requires a position name.`
					});
				}
				if (!position.positionStartDate) {
					errors.push({
						employmentIndex: employmentIndex,
						error: `Position ${positionIndex} in Employment ${employmentIndex} requires a start date.`
					});
				}
				if (position.stillWork === "No" && !position.positionEndDate) {
					errors.push({
						employmentIndex: employmentIndex,
						error: `Position ${positionIndex} in Employment ${employmentIndex} requires an end date.`
					});
				}
			});
		});
		return errors;
	};

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let uiErrors = [...isValid()];

		modelErrors = validateEmploymentsAndPositions();

		let allErrors = [...uiErrors, ...modelErrors];

		if (!allErrors.length) {
			showError = false;
			let response = await model.saveModelAsync(persistUrl);
			if (!response.success) {
				//console.log(displayError);
			} else {
				buttons.next();
			}
		} else {
			showError = true;

			if (modelErrors.length) {
				let newSelectedEmploymentIndex = 0;
				modelErrors.forEach((error) => {
					// If the current employment we are on has errors
					if (error.employmentIndex == selectedEmploymentIndex + 1) {
						newSelectedEmploymentIndex = -1;
					} else {
						// Using -1 to indicate that current employment has errors so we don't need to switch to a new one
						if (newSelectedEmploymentIndex != -1) {
							newSelectedEmploymentIndex = error.employmentIndex - 1;
						}
					}
				});

				// Switch selected employmentIndex to be one that has errors
				if (newSelectedEmploymentIndex !== -1) {
					selectEmployment(newSelectedEmploymentIndex);
				}

				showError = true;
				let scrollTarget = document.querySelector(".scrollTarget");
				scrollTarget.scrollIntoView({ behavior: "smooth" });
			}

			if (uiErrors.length) {
				uiErrors[0].scrollIntoView({ behavior: "smooth" });
			}
		}

		buttons.unblock();
	};

	const addEmployment = () => {
		$model.claimDetails.employments = [...$model.claimDetails.employments, new Employment()];
	};

	const addPosition = (employmentIndex) => {
		const employment = $model.claimDetails.employments[employmentIndex];
		employment.positions = [...employment.positions, new Position()];
		$model.claimDetails.employments = [
			...$model.claimDetails.employments.slice(0, employmentIndex),
			employment,
			...$model.claimDetails.employments.slice(employmentIndex + 1)
		];

		selectEmployment(selectedEmploymentIndex);
	};

	const deleteEmployment = (index) => {
		if ($model.claimDetails.employments.length <= 1) return;

		$model.claimDetails.employments.splice(index, 1);
		$model.claimDetails.employments = [...$model.claimDetails.employments];

		if (selectedEmploymentIndex == index) {
			selectedEmploymentIndex = 0;
		} else if (selectedEmploymentIndex > index) {
			selectedEmploymentIndex = selectedEmploymentIndex - 1;
		}

		selectEmployment(selectedEmploymentIndex);
	};

	const deletePosition = (positionIndex, employmentIndex) => {
		const employment = $model.claimDetails.employments[employmentIndex];
		if (employment.positions.length <= 1) return;

		employment.positions.splice(positionIndex, 1);
		$model.claimDetails.employments = [
			...$model.claimDetails.employments.slice(0, employmentIndex),
			employment,
			...$model.claimDetails.employments.slice(employmentIndex + 1)
		];

		selectEmployment(selectedEmploymentIndex);
	};

	const selectEmployment = (index) => {
		selectedEmploymentIndex = index;
		selectedEmployment = $model.claimDetails.employments[selectedEmploymentIndex];
		showError = false;
	};

	const economicLossToggle = (employmentIndex, id) => {
		const employments = $model.claimDetails.employments;
		const employment = employments[employmentIndex];
		const index = employment.economicLoss.findIndex((loss) => loss.economicLossTypeId === id);

		if (index !== -1) {
			employment.economicLoss.splice(index, 1);
		} else {
			const newEconomicLoss = new EconomicLoss();
			newEconomicLoss.economicLossTypeId = id;
			employment.economicLoss.push(newEconomicLoss);
		}
	};

	function economicLossIsChecked(employmentIndex, economicLossId) {
		const employment = $model.claimDetails.employments[employmentIndex];
		return employment.economicLoss.some((loss) => loss.economicLossTypeId === economicLossId);
	}

	function experiencedAfterIsChecked(employmentIndex, experiencedAfterId) {
		const employment = $model.claimDetails.employments[employmentIndex];
		return employment.experiencedAfter.some((after) => after.experiencedAfterTypeId === experiencedAfterId);
	}

	const experiencedAfterToggle = (employmentIndex, id, clear) => {
		console.log("Clear value", clear);

		const employments = $model.claimDetails.employments;
		const employment = employments[employmentIndex];

		if (clear) {
			// Clear all other selections
			employment.experiencedAfter = [];
			console.log("Experience after clear", employment.experiencedAfter);
			// Add only the "None of the above" option
			const newExperiencedAfter = new ExperiencedAfter();
			newExperiencedAfter.experiencedAfterTypeId = id;
			employment.experiencedAfter.push(newExperiencedAfter);
			noneOfAboveId = id;
		} else {
			// Remove "None of the above" if it is selected
			if (noneOfAboveId != null) {
				const noneOfAboveIndex = employment.experiencedAfter.findIndex(
					(loss) => loss.experiencedAfterTypeId === noneOfAboveId
				);
				if (noneOfAboveIndex !== -1) {
					employment.experiencedAfter.splice(noneOfAboveIndex, 1);
					noneOfAboveId = null;
				}
			}

			const index = employment.experiencedAfter.findIndex((loss) => loss.experiencedAfterTypeId === id);
			if (index !== -1) {
				// Remove the selected option if it is already in the array
				employment.experiencedAfter.splice(index, 1);
			} else {
				// Add the selected option
				const newExperiencedAfter = new ExperiencedAfter();
				newExperiencedAfter.experiencedAfterTypeId = id;
				employment.experiencedAfter.push(newExperiencedAfter);
			}
		}

		$model.claimDetails.employments.experiencedAfter = [...employment.experiencedAfter];
		dummyValue++;
	};

	const resetAgreementValues = () => {
		$model.claimDetails.enteredAgreement = null;
		$model.claimDetails.claimEnteredAgreement = null;
	};

	const initialize = () => {
		if (!$model.claimDetails) {
			$model.claimDetails = new ClaimDetails();
		}

		if ($model.claimDetails && !$model.claimDetails.employments) {
			$model.claimDetails.employments = [new Employment()];
		}

		// Select the initial employment
		selectEmployment(0);
	};

	initialize();
</script>

<PageSection>
	<Form {disabled} id="form-claim-details" on:submit={handleSubmit}>
		<FormRow class="confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Claim Details</h1>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		{#if $model.registrationType == registrationTypes.types.SomeoneElse.value}
			<p>
				The following questions are phrased towards you but should be answered on behalf of the person you are
				claiming for.
			</p>
		{/if}

		<FormRow>
			<FormColumn>
				<RadioGroup
					{disabled}
					required
					cols="6"
					label="Which of the following best identifies you?"
					name="genderTypes"
					bind:value={$model.claimDetails.gender}
					valueProperty="name"
					labelProperty="name"
					optionList={genderTypes}
				/>
			</FormColumn>
		</FormRow>
		<FormRow>
			<FormColumn>
				<YesNoGroup
					required
					{disabled}
					name="harrassmentExperienced"
					label="Do you think you may have experienced sexual harassment or discrimination against you in the workplace?"
					bind:value={$model.claimDetails.harrassmentExperienced}
				/>
			</FormColumn>
		</FormRow>

		{#if $model.claimDetails.harrassmentExperienced == "No"}
			<br />
			<div class="gray-background">
				<p>
					Thank you for your interest in this investigation. At this stage, our investigation only relates to
					individuals who may have experienced sexual harassment or discrimination in the workplace.
				</p>
				<p>
					If you believe you have a claim we might be able to assist with, please contact us via email at
					riotintoca@shine.com.au or by phone on 1800 961 050.
				</p>
			</div>
		{/if}

		<hr />
		{#if !disabled}
			{#if $model.claimDetails.harrassmentExperienced == "Yes"}
				<div class="confirmation-box-shadow">
					<FormRow>
						<FormColumn>
							<p>
								The following section will assist us in understanding the conditions of your employment
								when you believe you suffered sexual harassment or discrimination in the workforce. You
								can add multiple positions with the same employer.
							</p>
							<p>
								If you suffered sexual harassment or discrimination whilst employed by multiple
								employers, please use the button below “Add Employment” to list these separately.
							</p>
						</FormColumn>
					</FormRow>
					<FormRow>
						<FormColumn>
							<YesNoGroup
								required
								{disabled}
								name="takenAction"
								label="Have you taken any action against your employer or the person who sexually harassed or discriminated against you?"
								sublabel="Note: the action could include internal reporting to your HR or union representative, a complaint to the human rights commissioner, your own legal action, or something else"
								bind:value={$model.claimDetails.takenAction}
								onChanged={resetAgreementValues}
							/>
						</FormColumn>
					</FormRow>
					{#if $model.claimDetails.takenAction == "No"}
						<FormRow>
							<FormColumn>
								<YesNoGroup
									required
									{disabled}
									name="enteredAgreement"
									label="Have you entered into any agreements (such as a release agreement or non-disclosure agreement) with your employer or the person who sexually harassed or discriminated against you?"
									bind:value={$model.claimDetails.enteredAgreement}
								/>
							</FormColumn>
						</FormRow>
					{:else if $model.claimDetails.takenAction == "Yes"}
						<FormRow>
							<FormColumn>
								<YesNoGroup
									required
									{disabled}
									name="claimEnteredAgreement"
									label="As a result of that claim/those claims, have you entered into any agreements (such as a release agreement or non-disclosure agreement) with your employer or the person who sexually harassed or discriminated against you?"
									bind:value={$model.claimDetails.claimEnteredAgreement}
								/>
							</FormColumn>
						</FormRow>
					{/if}
					{#if $model.claimDetails.enteredAgreement == "Yes" || $model.claimDetails.claimEnteredAgreement == "Yes"}
						<br />
						<div class="gray-background">
							<p>
								Thank you for your interest in this investigation. Every person deserves to feel safe at
								work, and we are very sorry to hear about your experience with sexual harassment or
								discrimination in the workforce.
							</p>
							<p>
								We recommend you seek independent legal advice as to your ability to participate in this
								investigation. If you would like any more information, please feel free to contact our
								team for a confidential discussion by emailing riotintoca@shine.com.au or by phone on
								1800 961 050.
							</p>
						</div>
					{/if}
					{#if $model.claimDetails.enteredAgreement == "No" || ($model.claimDetails.claimEnteredAgreement == "No" && $model.claimDetails.takenAction != null)}
						<div class="scrollTarget"></div>
						<FormRow>
							<FormColumn cols="4" style="margin: 0;">
								<div class="spacer"></div>
								{#each $model.claimDetails.employments as employment, ei (employment)}
									<div class:tab={selectedEmploymentIndex == ei}>
										<FormRow
											style="margin-top: 0px; {selectedEmploymentIndex == ei
												? 'background-color: #edebeb;'
												: ''} {selectedEmploymentIndex == ei
												? 'border-radius: 6px 0 0 6px;'
												: ''}"
										>
											<FormColumn cols="10">
												<Tooltip align="left" content="Edit employment {ei + 1}">
													<!-- svelte-ignore a11y-click-events-have-key-events -->
													<div
														class="action borderBottom"
														on:click|preventDefault={() => selectEmployment(ei)}
													>
														{#if selectedEmploymentIndex == ei}
															<span class="orange">Employment {ei + 1}</span>
														{:else}
															<span>Employment {ei + 1}</span>
														{/if}
														{#if selectedEmploymentIndex != ei}
															<i style="margin-left: 2px;" class="fas fa-edit"></i>
														{/if}
													</div>
												</Tooltip>
											</FormColumn>

											<FormColumn cols="2">
												{#if $model.claimDetails.employments.length > 1 && !disabled}
													<Tooltip content="Delete employment {ei + 1}">
														<button
															class="action"
															{disabled}
															on:click|preventDefault={() => deleteEmployment(ei)}
															><i class="fas fa-trash"></i></button
														>
													</Tooltip>
												{/if}
											</FormColumn>
										</FormRow>
									</div>
								{/each}

								<FormRow>
									<FormColumn>
										<PrimaryButton on:click={() => addEmployment()} {disabled} submit={false}>
											Add Employment
										</PrimaryButton>
									</FormColumn>
								</FormRow>
							</FormColumn>

							<FormColumn
								cols="8"
								style="margin: 0; background-color: #edebeb; border-radius: 6px; padding-bottom: 20px;"
							>
								{#if selectedEmployment && selectedEmployment.positions}
									{#if showError}
										<p class="errorText">
											Please ensure all required fields are completed for this employment.
										</p>
									{/if}
									<FormRow>
										<FormColumn>
											<h4>Employment {selectedEmploymentIndex + 1}</h4>
										</FormColumn>
									</FormRow>
									<FormRow>
										<FormColumn>
											<TextInput
												required
												{disabled}
												label="Employer Name"
												sublabel="Note this may not be Rio Tinto if you were a sub-contractor or worked for a subsidiary company of Rio Tinto. 
                                    Please only include workplaces where you experienced sexual harassment or discrimination."
												bind:value={selectedEmployment.employerName}
												placeholder="Enter employer name here"
												id="employerName"
												maxlength="255"
												renderHtml={true}
											/>
										</FormColumn>
									</FormRow>
									{#each selectedEmployment.positions as position, pi (position)}
										<FormRow>
											<FormColumn>
												<TextInput
													required
													{disabled}
													label="Name of Your Position"
													bind:value={position.positionName}
													id="positionName_{pi}"
													maxlength="255"
												/>
											</FormColumn>
										</FormRow>

										<FormRow>
											<FormColumn>
												<DatePicker
													required
													{disabled}
													label="Start Date of Position"
													validator={true}
													start={new Date(1921, 0, 1)}
													end={new Date()}
													bind:formattedSelected={position.positionStartDate}
													openOnClass="calendar-icon"
													id="positionStartDate_{pi}"
													on:dateChange
													on:dateSelected
												/>
											</FormColumn>
										</FormRow>

										<FormRow>
											<FormColumn>
												<FormColumn>
													<YesNoGroup
														required
														{disabled}
														name="currentPosition_{pi}"
														label="Do you still work in this position?"
														bind:value={position.stillWork}
													/>
												</FormColumn>
											</FormColumn>
										</FormRow>

										{#if position.stillWork == "No"}
											<FormRow>
												<FormColumn>
													<DatePicker
														required
														{disabled}
														label="Finish Date of Position"
														validator={true}
														start={new Date(1921, 0, 1)}
														end={new Date()}
														bind:formattedSelected={position.positionEndDate}
														openOnClass="calendar-icon"
														id="positionEndDate_{pi}"
														on:dateChange
														on:dateSelected
													/>
												</FormColumn>
											</FormRow>
										{/if}

										<FormRow>
											<FormColumn>
												<TextInput
													{disabled}
													label="Location of Position eg. Hamersley Iron Mines at the Brockman 4 site"
													bind:value={position.positionLocation}
													id="positionLocation_{pi}"
													maxlength="255"
												/>
											</FormColumn>
										</FormRow>

										<FormRow>
											<FormColumn>
												{#if selectedEmployment.positions.length > 1}
													<RemoveButton
														{disabled}
														on:add={() => deletePosition(pi, selectedEmploymentIndex)}
														>Remove Position</RemoveButton
													>
													<hr />
												{/if}
											</FormColumn>
										</FormRow>
									{/each}

									<FormRow>
										<FormColumn>
											<AddButton {disabled} on:add={() => addPosition(selectedEmploymentIndex)}
												>Add another position with same employer</AddButton
											>
										</FormColumn>
									</FormRow>

									<FormRow>
										<FormColumn>
											<Select
												{disabled}
												label="State of Employment"
												bind:value={selectedEmployment.stateId}
												textProperty="lookupName"
												valueProperty="id"
												optionList={$lookups.state}
												required
											></Select>
										</FormColumn>
									</FormRow>

									<FormRow>
										<FormColumn>
											<TextArea
												required
												{disabled}
												label="Please tell us briefly about the sexual harassment or discrimination you were subjected to."
												bind:value={selectedEmployment.harrassmentDetails}
												id="harassmentDetails"
												maxlength="3000"
											/>
										</FormColumn>
									</FormRow>

									<FormRow>
										<FormColumn>
											<YesNoGroup
												required
												{disabled}
												name="reportedHarassment"
												label="Did you report the sexual harassment or discrimination?"
												bind:value={selectedEmployment.harassmentReported}
											/>
										</FormColumn>
									</FormRow>

									{#if selectedEmployment.harassmentReported == "Yes"}
										<FormRow>
											<FormColumn>
												<TextInput
													required
													{disabled}
													label="Who did you report the sexual harassment to?"
													bind:value={selectedEmployment.harrassmentReportedTo}
													id="harassmentReportedTo"
													maxlength="255"
												/>
											</FormColumn>
										</FormRow>

										<FormRow>
											<FormColumn>
												<YesNoGroup
													required
													{disabled}
													name="complaintResolved"
													label="Was your complaint resolved?"
													bind:value={selectedEmployment.complaintResolved}
												/>
											</FormColumn>
										</FormRow>
									{/if}

									{#if selectedEmployment.complaintResolved == "Yes"}
										<FormRow>
											<FormColumn>
												<YesNoGroup
													required
													{disabled}
													name="satisfiedWithResolution"
													label="Were you satisfied with the resolution of your complaint?"
													bind:value={selectedEmployment.complaintSatisfied}
												/>
											</FormColumn>
										</FormRow>
									{/if}

									<FormRow>
										<FormColumn>
											<TextArea
												{disabled}
												label="Is there any other information you would like to provide regarding your complaint?"
												bind:value={selectedEmployment.additionalInformation}
												id="additionalInformation"
												maxlength="3000"
											/>
										</FormColumn>
									</FormRow>

									<FormRow>
										<FormColumn>
											<YesNoGroup
												required
												{disabled}
												name="SufferEconomicLoss"
												label="Did you suffer financial loss as a result of your experience of sexual harassment or discrimination?"
												bind:value={selectedEmployment.sufferEconomicLoss}
											/>
										</FormColumn>
									</FormRow>

									{#if selectedEmployment.sufferEconomicLoss == "Yes"}
										<FormRow>
											<FormColumn>
												<h5>Please select any of the following that apply</h5>
											</FormColumn>
										</FormRow>
										{#each $lookups.economicLossType as economicLoss}
											<FormRow>
												<FormColumn>
													<Checkbox
														{disabled}
														id="EconomicLoss_{economicLoss.id}"
														label={economicLoss.lookupName}
														on:change={economicLossToggle(
															selectedEmploymentIndex,
															economicLoss.id
														)}
														checked={economicLossIsChecked(
															selectedEmploymentIndex,
															economicLoss.id
														)}
													/>
												</FormColumn>
											</FormRow>
										{/each}
										<FormRow>
											<FormColumn>
												<TextArea
													{disabled}
													label="Is there any other information you would like to provide regarding the economic loss you suffered?"
													bind:value={selectedEmployment.economicLossAdditional}
													id="EconomicLossAdditional"
													maxlength="3000"
												/>
											</FormColumn>
										</FormRow>
									{/if}

									<FormRow>
										<FormColumn>
											<!-- svelte-ignore a11y-label-has-associated-control -->
											<label
												>Have you experienced any of the following after your experience of
												sexual harassment or discrimination? <span class="required">*</span
												></label
											>
											<br />
											<h5>Please select any of the following that apply</h5>
										</FormColumn>
									</FormRow>
									{#key dummyValue}
										{#each $lookups.experiencedAfterType as experiencedAfter (experiencedAfter.id)}
											<FormRow>
												<FormColumn>
													<Checkbox
														{disabled}
														id="ExperiencedAfter_{experiencedAfter.id}"
														label={experiencedAfter.lookupName}
														on:change={() =>
															experiencedAfterToggle(
																selectedEmploymentIndex,
																experiencedAfter.id,
																experiencedAfter.lookupName ===
																	"None of the above apply to me"
															)}
														checked={experiencedAfterIsChecked(
															selectedEmploymentIndex,
															experiencedAfter.id
														)}
													/>
												</FormColumn>
											</FormRow>
										{/each}
									{/key}
									<FormRow>
										<FormColumn>
											<TextArea
												{disabled}
												label="Is there any other information you would like to provide regarding the impact of the sexual harassment or discrimination you experienced?"
												bind:value={selectedEmployment.experiencedAfterAdditional}
												id="experiencedAfterAdditional"
												maxlength="3000"
											/>
										</FormColumn>
									</FormRow>
								{/if}
							</FormColumn>
						</FormRow>
					{/if}
				</div>
			{/if}
		{:else if $model.claimDetails.enteredAgreement == "No" || ($model.claimDetails.claimEnteredAgreement == "No" && $model.claimDetails.takenAction != null)}
			<div class="confirmation-box-shadow">
				<FormRow>
					<FormColumn>
						<p>
							The following section will assist us in understanding the conditions of your employment when
							you suffered sexual harassment or discrimination in the workforce. You can add multiple
							positions with the same employer.
						</p>
						<p>
							If you suffered sexual harassment or discrimination whilst employed by multiple different
							employers, please use the button below “Add Employment” to list these separately.
						</p>
					</FormColumn>
				</FormRow>
				<FormRow>
					<FormColumn>
						<YesNoGroup
							required
							{disabled}
							name="takenAction"
							label="Have you taken any action against your employer or the person who sexually harassed or discriminated against you?"
							sublabel="Note: the action could include internal reporting to your HR or union representative, a complaint to the human rights commissioner, your own legal action, or something else"
							bind:value={$model.claimDetails.takenAction}
							onChanged={resetAgreementValues}
						/>
					</FormColumn>
				</FormRow>
				{#if $model.claimDetails.takenAction == "No"}
					<FormRow>
						<FormColumn>
							<YesNoGroup
								required
								{disabled}
								name="enteredAgreement"
								label="Have you entered into any agreements (such as a release agreement or non-disclosure agreement) with your employer or the person who sexually harassed or discriminated against you?"
								bind:value={$model.claimDetails.enteredAgreement}
							/>
						</FormColumn>
					</FormRow>
				{:else if $model.claimDetails.takenAction == "Yes"}
					<FormRow>
						<FormColumn>
							<YesNoGroup
								required
								{disabled}
								name="claimEnteredAgreement"
								label="As a result of that claim/those claims, have you entered into any agreements (such as a release agreement or non-disclosure agreement) with your employer or the person who sexually harassed or discriminated against you?"
								bind:value={$model.claimDetails.claimEnteredAgreement}
							/>
						</FormColumn>
					</FormRow>
				{/if}
				<FormRow>
					<FormColumn cols="12">
						{#each $model.claimDetails.employments as employment, ei}
							<FormRow>
								<FormColumn>
									<h4>Employment {ei + 1}</h4>
								</FormColumn>
							</FormRow>
							<FormRow>
								<FormColumn>
									<TextInput
										required
										{disabled}
										label="Employer Name"
										sublabel="Note this may not be Rio Tinto if you were a sub-contractor or worked for a subsidiary company of Rio Tinto. Please only include workplaces where you experienced sexual harassment or discrimination."
										bind:value={employment.employerName}
										placeholder="Enter employer name here"
										id={`employerName_${ei}`}
										renderHtml={true}
									/>
								</FormColumn>
							</FormRow>
							{#each employment.positions as position, pi}
								<FormRow>
									<FormColumn>
										<TextInput
											required
											{disabled}
											label="Name of Your Position"
											bind:value={position.positionName}
											id={`positionName_${ei}_${pi}`}
										/>
									</FormColumn>
								</FormRow>
								<FormRow>
									<FormColumn>
										<DatePicker
											required
											{disabled}
											label="Start Date of Position"
											validator={true}
											start={new Date(1921, 0, 1)}
											end={new Date()}
											bind:formattedSelected={position.positionStartDate}
											openOnClass="calendar-icon"
											id={`positionStartDate_${ei}_${pi}`}
											on:dateChange
											on:dateSelected
										/>
									</FormColumn>
								</FormRow>
								<FormRow>
									<FormColumn>
										<YesNoGroup
											required
											{disabled}
											name={`currentPosition_${ei}_${pi}`}
											label="Do you still work in this position?"
											bind:value={position.stillWork}
										/>
									</FormColumn>
								</FormRow>
								{#if position.stillWork == "No"}
									<FormRow>
										<FormColumn>
											<DatePicker
												required
												{disabled}
												label="Finish Date of Position"
												validator={true}
												start={new Date(1921, 0, 1)}
												end={new Date()}
												bind:formattedSelected={position.positionEndDate}
												openOnClass="calendar-icon"
												id={`positionEndDate_${ei}_${pi}`}
												on:dateChange
												on:dateSelected
											/>
										</FormColumn>
									</FormRow>
								{/if}
								<FormRow>
									<FormColumn>
										<TextInput
											{disabled}
											label="Location of Position eg. Hamersley Iron Mines at the Brockman 4 site"
											bind:value={position.positionLocation}
											id={`positionLocation_${ei}_${pi}`}
										/>
									</FormColumn>
								</FormRow>
							{/each}
							<FormRow>
								<FormColumn>
									<Select
										{disabled}
										label="State of Employment"
										bind:value={employment.stateId}
										textProperty="lookupName"
										valueProperty="id"
										optionList={$lookups.state}
										required
										id={`stateOfEmployment_${ei}`}
									></Select>
								</FormColumn>
							</FormRow>

							<FormRow>
								<FormColumn>
									<TextArea
										required
										{disabled}
										label="Please tell us briefly about the sexual harassment or discrimination you were subjected to."
										bind:value={employment.harrassmentDetails}
										id={`harassmentDetails_${ei}`}
										maxlength="3000"
									/>
								</FormColumn>
							</FormRow>

							<FormRow>
								<FormColumn>
									<YesNoGroup
										required
										{disabled}
										name={`reportedHarassment_${ei}`}
										label="Did you report the sexual harassment or discrimination?"
										bind:value={employment.harassmentReported}
										id={`reportedHarassment_${ei}`}
									/>
								</FormColumn>
							</FormRow>

							{#if employment.harassmentReported == "Yes"}
								<FormRow>
									<FormColumn>
										<TextInput
											required
											{disabled}
											label="Who did you report the sexual harassment to?"
											bind:value={employment.harrassmentReportedTo}
											id={`harassmentReportedTo_${ei}`}
										/>
									</FormColumn>
								</FormRow>

								<FormRow>
									<FormColumn>
										<YesNoGroup
											required
											{disabled}
											name={`complaintResolved_${ei}`}
											label="Was your complaint resolved?"
											bind:value={employment.complaintResolved}
											id={`complaintResolved_${ei}`}
										/>
									</FormColumn>
								</FormRow>
							{/if}

							{#if employment.complaintResolved == "Yes"}
								<FormRow>
									<FormColumn>
										<YesNoGroup
											required
											{disabled}
											name={`satisfiedWithResolution_${ei}`}
											label="Were you satisfied with the resolution of your complaint?"
											bind:value={employment.complaintSatisfied}
											id={`satisfiedWithResolution_${ei}`}
										/>
									</FormColumn>
								</FormRow>
							{/if}

							<FormRow>
								<FormColumn>
									<TextArea
										{disabled}
										label="Is there any other information you would like to provide?"
										bind:value={employment.additionalInformation}
										id={`additionalInformation_${ei}`}
										maxlength="3000"
									/>
								</FormColumn>
							</FormRow>

							<FormRow>
								<FormColumn>
									<YesNoGroup
										required
										{disabled}
										name="SufferEconomicLoss_${ei}"
										label="Did you suffer financial loss as a result of your experience of sexual harassment or discrimination?"
										bind:value={employment.sufferEconomicLoss}
									/>
								</FormColumn>
							</FormRow>

							{#if employment.sufferEconomicLoss == "Yes"}
								<FormRow>
									<FormColumn>
										<h5>Please select any of the following that apply</h5>
									</FormColumn>
								</FormRow>
								{#each $lookups.economicLossType as economicLoss}
									<FormRow>
										<FormColumn>
											<Checkbox
												{disabled}
												id="EconomicLoss_{economicLoss.id}_${ei}"
												label={economicLoss.lookupName}
												checked={economicLossIsChecked(ei, economicLoss.id)}
											/>
										</FormColumn>
									</FormRow>
								{/each}
								<FormRow>
									<FormColumn>
										<TextArea
											{disabled}
											label="Is there any other information you would like to provide?"
											bind:value={employment.economicLossAdditional}
											id="EconomicLossAdditional_${ei}"
											maxlength="3000"
										/>
									</FormColumn>
								</FormRow>
							{/if}

							<!-- <FormRow>
                        <FormColumn>
                            <YesNoGroup
                                required
                                {disabled}
                                name="didExperiencedAfter_${ei}"
                                label="Have you experienced any of the following after your experience of sexual harassment or discrimination?"
                                bind:value={employment.didExperiencedAfter} />
                        </FormColumn>
                    </FormRow> -->

							<!-- {#if employment.didExperiencedAfter == 'Yes'} -->
							<FormRow>
								<FormColumn>
									<!-- svelte-ignore a11y-label-has-associated-control -->
									<label
										>Have you experienced any of the following after your experience of sexual
										harassment or discrimination?</label
									>
								</FormColumn>
							</FormRow>
							<FormRow>
								<FormColumn>
									<h5>Please select any of the following that apply</h5>
								</FormColumn>
							</FormRow>
							{#each $lookups.experiencedAfterType as experiencedAfter}
								<FormRow>
									<FormColumn>
										<Checkbox
											{disabled}
											id="ExperiencedAfter_{experiencedAfter.id}_${ei}"
											label={experiencedAfter.lookupName}
											checked={experiencedAfterIsChecked(ei, experiencedAfter.id)}
										/>
									</FormColumn>
								</FormRow>
							{/each}
							<FormRow>
								<FormColumn>
									<TextArea
										{disabled}
										label="Is there any other information you would like to provide?"
										bind:value={employment.experiencedAfterAdditional}
										id="experiencedAfterAdditional_${ei}"
										maxlength="3000"
									/>
								</FormColumn>
							</FormRow>
							<!-- {/if}       -->
						{/each}
					</FormColumn>
				</FormRow>
			</div>
		{/if}

		{#if $model.claimDetails.harrassmentExperienced == "Yes" && ($model.claimDetails.enteredAgreement == "No" || ($model.claimDetails.claimEnteredAgreement == "No" && $model.claimDetails.takenAction != null))}
			<FormRow class="confirmation-hide">
				<FormColumn class="last">
					<SaveAndClose>Save and Close</SaveAndClose>
				</FormColumn>
			</FormRow>

			<FormRow class="confirmation-hide">
				<FormColumn class="last">
					<NavigationButtons
						bind:buttons
						nextPageLabel={$model.confirmationPage ? "Confirmation" : "Next"}
						tealiumTag={formTypes.Registration}
						pageName={title}
					/>
				</FormColumn>
			</FormRow>
		{/if}
	</Form>
</PageSection>

<style>
	.gray-background {
		background-color: #cfcfcf;
		padding: 10px;
	}

	.action {
		outline: none;
		background-color: transparent;
		border: none;
	}
	.action:hover {
		cursor: pointer;
	}

	.orange {
		color: #ff4814;
	}

	.borderBottom {
		margin-bottom: 6px;
	}

	.errorText {
		color: red;
		font-style: italic;
		text-align: center;
	}

	.spacer {
		height: 40px;
	}
</style>
